import React, { useEffect, useState } from "react";
import {
    Box,
    Button,
    Card,
    CardContent,
    CircularProgress,
    InputLabel,
    MenuItem,
    Select,
    TextField,
    Typography,
} from "@mui/material";
import { useParams } from "react-router-dom";
import { doc, getDoc } from "firebase/firestore";
import { OrganizationType, updateOrganization, uploadOrganizationLogo } from "../../components/utils/Organization";
import { SketchPicker } from "react-color";
import { db } from "../../config/firebase";
import { ColorPickerField } from "../../components/ColorPickerField";

export const EditOrganizationPage: React.FC = () => {
    const { orgId } = useParams<{ orgId: string }>();

    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const [name, setName] = useState("");
    const [type, setType] = useState<OrganizationType>("school");
    const [primaryColor, setPrimaryColor] = useState("#1976d2");
    const [secondaryColor, setSecondaryColor] = useState("#fbc02d");
    const [logoFile, setLogoFile] = useState<File | null>(null);
    const [logoPreview, setLogoPreview] = useState<string | null>(null);

    useEffect(() => {
        const fetchOrg = async () => {
            try {
                const ref = doc(db, "organizations", orgId!);
                const snap = await getDoc(ref);
                if (!snap.exists()) {
                    setError("Organization not found.");
                    return;
                }

                const data = snap.data();
                setName(data.name);
                setType(data.type);
                setPrimaryColor(data.primaryColor ?? "#1976d2");
                setSecondaryColor(data.secondaryColor ?? "#fbc02d");
                if (data.logoUrl) {
                    setLogoPreview(data.logoUrl);
                }
            } catch (err) {
                console.error(err);
                setError("Failed to load organization.");
            } finally {
                setLoading(false);
            }
        };

        if (orgId) {
            fetchOrg();
        }
    }, [orgId]);

    const handleLogoChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (file) {
            setLogoFile(file);
            setLogoPreview(URL.createObjectURL(file));
        }
    };

    const handleSave = async () => {
        setSaving(true);
        try {
            const updates: any = {
                name,
                type,
                primaryColor,
                secondaryColor,
            };

            if (logoFile) {
                const logoUrl = await uploadOrganizationLogo(logoFile, orgId!);
                updates.logoUrl = logoUrl;
            }

            await updateOrganization(orgId!, updates);
        } catch (err: any) {
            console.error(err);
            setError("Failed to save organization.");
        } finally {
            setSaving(false);
        }
    };

    if (loading) return <CircularProgress />;
    if (error) return <Typography color="error">{error}</Typography>;

    return (
        <Card sx={{ maxWidth: 600, margin: "auto", mt: 4 }}>
            <CardContent>
                <Typography variant="h5" gutterBottom>
                    Edit Organization
                </Typography>

                <Box display="flex" flexDirection="column" gap={2}>
                    <TextField
                        label="Organization Name"
                        fullWidth
                        value={name}
                        onChange={(e) => setName(e.target.value)}
                    />

                    <Box>
                        <InputLabel id="org-type-label">Organization Type</InputLabel>
                        <Select
                            labelId="org-type-label"
                            value={type}
                            fullWidth
                            onChange={(e) => setType(e.target.value as OrganizationType)}
                        >
                            {["school", "club", "league"].map((option) => (
                                <MenuItem key={option} value={option}>
                                    {option.charAt(0).toUpperCase() + option.slice(1)}
                                </MenuItem>
                            ))}
                        </Select>
                    </Box>

                    <Box>
                        <ColorPickerField
                            label="Primary Color"
                            color={primaryColor}
                            onChange={(color) => { setPrimaryColor(color) }}
                        />
                    </Box>

                    <Box>
                        <ColorPickerField
                            label="Secondary Color"
                            color={secondaryColor}
                            onChange={(color) => { setSecondaryColor(color) }}
                        />
                    </Box>

                    <Box>
                        <InputLabel>Logo</InputLabel>
                        <input type="file" accept="image/*" onChange={handleLogoChange} />
                        {logoPreview && (
                            <Box mt={2}>
                                <img
                                    src={logoPreview}
                                    alt="Logo Preview"
                                    style={{ maxWidth: "100%", maxHeight: 150 }}
                                />
                            </Box>
                        )}
                    </Box>

                    <Button variant="contained" onClick={handleSave} disabled={saving}>
                        {saving ? <CircularProgress size={24} /> : "Save Changes"}
                    </Button>
                </Box>
            </CardContent>
        </Card>
    );
};
